import type { MessageKey } from './en';

const de: Record<MessageKey, string> = {
  'common.quoted': '„{value}"',
  'common.uciIdMissing': 'ohne UCI-ID',
  'common.unknownPlaceholder': '?',
  'common.sexWoman': 'weiblich',
  'common.sexMan': 'männlich',
  'common.optional': 'optional',
  'common.edit': 'Bearbeiten',
  'common.delete': 'Löschen',
  'common.done': 'Fertig',
  'common.cancel': 'Abbrechen',
  'common.close': 'Schließen',
  'common.reset': 'Zurücksetzen',
  'common.language': 'Sprache',
  'common.theme': 'Erscheinungsbild',
  'common.theme.system': 'System',
  'common.theme.light': 'Hell',
  'common.theme.dark': 'Dunkel',
  'common.back': 'Zurück',

  'starterType.single': '1er (Einzel)',
  'starterType.pair': '2er (Zweier)',
  'starterType.team4': '4er (Vierermannschaft)',
  'starterType.team6': '6er (Sechsermannschaft)',

  'ageTier.eliteJunior': 'Elite / Junioren',
  'ageTier.youth': 'Schüler',

  'app.title': 'Kür',
  'app.subtitle': 'Kunstrad Meldetool',
  'app.nav': 'Hauptnavigation',
  'app.menu': 'Menü',
  'app.drawerMenu': 'Navigationsmenü',
  'app.open': 'Öffnen',
  'app.save': 'Sichern',
  'app.importSelection': 'Import…',
  'app.exportSelection': 'Export…',
  'app.weboImport': 'WeBo-Import…',
  'app.settings': 'Einstellungen',
  'app.about': 'Über',
  'app.mainMissing': 'Hauptbereich fehlt.',
  'app.opened': '{file} geöffnet.',
  'app.openFailed': 'Diese Datei lässt sich nicht als Arbeitsbereich lesen.',
  'nav.home': 'Startseite',
  'nav.registrationData': 'Meldedaten',
  'nav.competitions': 'Wettbewerbe',
  'nav.clubs': 'Vereine',
  'nav.athletes': 'Sportler',
  'nav.starter': 'Starter',
  'nav.programme': 'Programm',
  'nav.entries': 'Meldungen',
  'nav.impressum': 'Impressum',
  'nav.datenschutz': 'Datenschutz',

  'home.welcome.title': 'Willkommen bei Kür',
  'home.welcome.text':
    'Kür hilft dir dabei, Sportlerinnen und Sportler sowie ihre Wettkampfprogramme für den Kunstradsport anzumelden — von der Kür bis zum fertigen XML-Export.',
  'home.howto.title': "So geht's",
  'home.howto.step1': '[Verein:clubs] und [Sportler:athletes] anlegen',
  'home.howto.step2': 'Kür aus dem [Figurenkatalog:programme] zusammenstellen',
  'home.howto.step3': 'Zu einem [Wettbewerb:competitions] anmelden und XML [exportieren:entries]',
  'home.start': "Los geht's",
  'home.importExport.title': 'Import & Export',
  'home.importExport.text':
    'Oben in der Kopfleiste (auf dem Handy im „⋮“-Menü) kannst du den ganzen Arbeitsbereich als Datei [öffnen:open] oder [sichern:save], oder nur einen Teil davon [importieren:import] bzw. [exportieren:export] — etwa nur die Sportler oder ein einzelnes Programm. Dieselben Funktionen findest du auch unten auf der [Meldungen:entries]-Seite.',
  'home.privacy.title': 'Deine Daten bleiben auf deinem Gerät',
  'home.privacy.text':
    'Kür funktioniert vollständig offline — alles, was du eingibst, wird ausschließlich lokal im Speicher deines Browsers auf diesem Gerät abgelegt. Niemand sonst, auch wir nicht, kann diese Daten einsehen. Das bedeutet aber auch: Du bist selbst dafür verantwortlich, deinen Arbeitsbereich als Datei zu sichern und aufzubewahren — es gibt keine Sicherung an anderer Stelle.',
  'home.install.title': 'Kür installieren',
  'home.install.already': 'Du nutzt die installierte App — sie funktioniert offline und öffnet sich in einem eigenen Fenster.',
  'home.install.button': 'Installieren',
  'home.install.prompt': 'Installiere Kür als App für schnellen Zugriff und vollständige Offline-Nutzung, ganz ohne Browserleiste.',
  'home.install.ios':
    'Tippe auf das Teilen-Symbol und dann auf „Zum Home-Bildschirm" — Kür öffnet sich danach wie jede andere App und funktioniert vollständig offline.',
  'home.install.macSafari':
    'Öffne das Teilen-Menü (oder Ablage → Zum Dock hinzufügen) und wähle „Zum Dock hinzufügen" — Kür öffnet sich danach wie jede andere App und funktioniert vollständig offline.',
  'home.install.android':
    'Öffne das Browsermenü und suche nach „Zum Startbildschirm hinzufügen" oder „App installieren" — Kür öffnet sich danach wie jede andere App und funktioniert vollständig offline.',
  'home.install.desktop':
    'Halte Ausschau nach einem Installieren-Symbol in der Adressleiste oder suche im Browsermenü nach „Kür installieren…" — die App öffnet sich danach in einem eigenen Fenster und funktioniert vollständig offline.',
  'home.install.unsupported':
    'Dein Browser bietet keine Installation von Kür als App an, aber sie funktioniert trotzdem vollständig offline, sobald du sie einmal hier geöffnet hast.',

  'about.title': 'Über Kür',
  'about.story.title': 'Warum es Kür gibt',
  'about.story.p1': 'Meine Freundin ist mit dem Kunstradsport aufgewachsen und liebt diesen Sport von ganzem Herzen. Vor einiger Zeit hat sie sich entschieden, mit ihrer Partnerin einen zweiten Anlauf im Wettkampfsport zu nehmen. Immer wieder haben die beiden stundenlang zusammengesessen, um gemeinsam ihre Küren zusammenzustellen und ich habe lange nicht verstanden, warum das immer so lange dauert.',
  'about.story.p2': 'Dann habe ich mir die Werkzeuge angeschaut, die dafür heute noch verwendet werden — solide für ihre Zeit gebaut, aber inzwischen nicht mehr auf dem Stand der Technik, und jede Änderung dauerte länger, als sie müsste. Wenn selbst zwei erfahrene Sportlerinnen damit zu tun hatten, dürfte es Neueinsteigern kaum leichter fallen. Als ehemaliger Leiter einer Software-Entwicklung dachte ich mir: Das geht heute einfacher.',
  'about.story.p3': 'Also habe ich, mit Unterstützung von Claude, eine mobile-first Web-App gebaut, mit der sich Programme schnell zusammenstellen, testen und wieder verwerfen lassen — mit eingebauten Prüfungen und einer Visualisierung des Figurenflusses, damit der Aufbau einer Kür auf einen Blick verständlich wird. So ist Kür entstanden.',
  'about.howto.title': 'Eine Meldung aufbauen',
  'about.howto.lede': 'Die Bildschirme in der Navigation folgen dieser Reihenfolge, lassen sich aber jederzeit in beliebiger Reihenfolge aufrufen — nichts muss abgeschlossen sein, bevor es weitergeht.',
  'about.howto.registrationData': 'Meldedaten — der eigene Verein und die für die Meldung verantwortliche Kontaktperson.',
  'about.howto.competitions': 'Wettbewerbe — jeder Wettbewerb, für den gemeldet werden soll.',
  'about.howto.clubs': 'Vereine — jeder teilnehmende Verein, einmal angelegt und danach überall nur noch ausgewählt.',
  'about.howto.athletes': 'Sportler — jeder Athlet, einem Verein zugeordnet und später einem Starter.',
  'about.howto.starter': 'Starter — wer zusammen antritt: 1er, 2er, 4er- oder 6er-Mannschaft.',
  'about.howto.programme': 'Programm — die geordnete Figurenfolge, die ein Starter zeigt, zusammengestellt aus dem Übungskatalog.',
  'about.howto.entries': 'Meldungen — verknüpfen einen Starter, ein Programm und einen Wettbewerb; jede Meldung erzeugt ihren eigenen Export.',
  'about.howto.footnote': 'Eine Meldung im Bildschirm „Meldungen" öffnen, um sie gegen beide Schemas zu prüfen und die XML herunterzuladen.',
  'about.sbom.title': 'Open-Source-Komponenten',
  'about.sbom.lede': 'Kür basiert auf den unten aufgeführten Open-Source-Bibliotheken, automatisch aus der package.json des Projekts erzeugt — diese Liste ist immer aktuell.',
  'about.sbom.runtime': 'An den Browser ausgeliefert',
  'about.sbom.build': 'Nur Build-Werkzeuge',
  'about.support.title': 'Kür unterstützen',
  'about.support.text': 'Ich verdiene mit Kür kein Geld — mir geht es einzig darum, dass die Kunstrad-Familie sich von den alten Werkzeugen verabschieden kann. Hosting und die Bereitstellung in den App Stores kosten mich trotzdem etwa 200 € im Jahr. Wenn dir Kür hilft, freue ich mich über einen Kaffee. Manche App-Versionen zeigen dafür ein kleines, dezentes Werbebanner, das sich über einen kleinen In-App-Kauf entfernen lässt — beides bleibt optional und ändert nichts an der eigentlichen Nutzung von Kür.',

  'legal.impressum.placeholder':
    'Angaben gemäß § 5 DDG (Digitale-Dienste-Gesetz)\n\nBenedikt-Alexander Mokroß\nc/o Impressumservice Dein-Impressum\nStettiner Str. 41\n35410 Hungen\nDeutschland\n\nKontakt\nE-Mail: info@kuer.eu\n\nKür ist ein privates, nicht-kommerzielles Projekt.\n\nHaftung für Inhalte\nAls Diensteanbieter bin ich gemäß § 7 Abs. 1 DDG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 DDG bin ich als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben davon unberührt; eine Haftung ist erst ab Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden entsprechender Rechtsverletzungen entferne ich die betroffenen Inhalte umgehend.\n\nHaftung für Links\nDiese App kann auf externe Webseiten Dritter verweisen, auf deren Inhalte ich keinen Einfluss habe. Für diese fremden Inhalte kann ich daher keine Gewähr übernehmen; verantwortlich ist stets der jeweilige Anbieter der verlinkten Seite. Verlinkte Seiten wurden zum Zeitpunkt der Verlinkung auf erkennbare Rechtsverstöße geprüft. Eine permanente inhaltliche Kontrolle ohne konkreten Anlass ist nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen entferne ich solche Links umgehend.\n\nUrheberrecht\nDie von mir erstellten Inhalte auf diesen Seiten unterliegen dem deutschen Urheberrecht. Beiträge Dritter sind als solche gekennzeichnet. Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechts bedürfen meiner schriftlichen Zustimmung.',
  'legal.datenschutz.placeholder':
    'Datenschutzerklärung gemäß DSGVO\n\nVerantwortlicher\nBenedikt-Alexander Mokroß\nc/o Impressumservice Dein-Impressum\nStettiner Str. 41\n35410 Hungen\nDeutschland\nE-Mail: info@kuer.eu\n\nÜbersicht\nKür verarbeitet praktisch keine personenbezogenen Daten auf einem Server. Dein Arbeitsbereich — Vereine, Sportler, Starter, Programme, Meldungen — wird ausschließlich lokal in deinem Browser gespeichert und nie automatisch übertragen. Es gibt kein Nutzerkonto, keine Anmeldung und keine serverseitige Speicherung deiner Arbeitsbereichsdaten.\n\nLokale Speicherung im Browser\nDie App speichert im Local Storage deines Geräts deinen aktuellen Arbeitsbereich, deine Sprach- und Design-Einstellung sowie deine Cookie-Einstellungen. Diese Daten verlassen dein Gerät nicht, außer du exportierst sie selbst als Datei oder erzeugst eine XML-Meldung zum Download. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO für deinen Arbeitsbereich (Erfüllung des von dir gewünschten Zwecks der App) und Art. 6 Abs. 1 lit. f DSGVO für Komfortfunktionen wie Sprache und Design (berechtigtes Interesse an einer funktionierenden Anwendung). Diese Daten bleiben gespeichert, bis du sie löschst oder deinen Browser-Speicher leerst — es gibt keine automatische Löschfrist und keine serverseitige Aufbewahrung, weil es keine serverseitige Speicherung gibt.\n\nCookie-Einstellungen\nBeim ersten Besuch fragt die App, ob zusätzliche, aktuell noch nicht umgesetzte Funktionen — Nutzungsstatistik und Absturz-/Fehlerberichte — künftig aktiv werden dürfen. Deine Wahl wird lokal und zeitlich unbegrenzt gespeichert (siehe oben) und ist jederzeit über „Cookie-Einstellungen“ im Menü änderbar. Solange diese Funktionen nicht umgesetzt sind, hat deine Auswahl keine Wirkung: Es werden keine entsprechenden Daten erhoben oder übertragen.\n\nSchriftarten\nDie verwendeten Schriftarten Archivo und IBM Plex Mono werden mit der App ausgeliefert und von unserem eigenen Server geladen. Es besteht keine Verbindung zu Google Fonts oder anderen Schriftarten-Anbietern.\n\nHosting und Server-Logfiles\nDiese Website wird bei der STRATO GmbH gehostet. Beim Aufruf der Seite verarbeitet der Hoster automatisch technische Zugriffsdaten — u. a. IP-Adresse, Datum und Uhrzeit, aufgerufene Datei, übertragene Datenmenge, Browser/Betriebssystem und Referrer-URL —, wie es bei jedem Webserver-Betrieb technisch notwendig ist. Diese Logfiles dienen der technischen Bereitstellung, Stabilität und Sicherheit der Website und werden nicht mit anderen Datenquellen zusammengeführt. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einem funktionsfähigen, sicheren Betrieb). Soweit gesetzlich erforderlich, besteht mit STRATO ein Vertrag zur Auftragsverarbeitung nach Art. 28 DSGVO.\n\nKontaktaufnahme per E-Mail\nSchreibst du mir per E-Mail, verarbeite ich die mitgeteilten Daten — insbesondere deine E-Mail-Adresse und den Inhalt deiner Nachricht — ausschließlich zur Bearbeitung deiner Anfrage (Art. 6 Abs. 1 lit. b bzw. lit. f DSGVO). Ich lösche diese Daten, sobald sie nicht mehr benötigt werden, soweit keine gesetzlichen Aufbewahrungspflichten entgegenstehen.\n\nWeitergabe an Dritte\nEs findet keine Weitergabe deiner Daten an Dritte statt, außer der unter „Hosting und Server-Logfiles“ genannten, technisch notwendigen Verarbeitung durch den Hosting-Anbieter.\n\nKeine automatisierte Entscheidungsfindung\nEs findet keine automatisierte Entscheidungsfindung oder Profiling im Sinne von Art. 22 DSGVO statt.\n\nDeine Rechte\nDu hast das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16 DSGVO), Löschung (Art. 17 DSGVO), Einschränkung der Verarbeitung (Art. 18 DSGVO), Datenübertragbarkeit (Art. 20 DSGVO) und Widerspruch gegen die Verarbeitung (Art. 21 DSGVO). Eine Einwilligung kannst du jederzeit mit Wirkung für die Zukunft widerrufen (Art. 7 Abs. 3 DSGVO), etwa über die Cookie-Einstellungen. Außerdem hast du das Recht, dich bei einer Datenschutz-Aufsichtsbehörde zu beschweren (Art. 77 DSGVO) — zuständig ist in der Regel der Hessische Beauftragte für Datenschutz und Informationsfreiheit (HBDI), da mein Sitz in Hessen liegt.\n\nÄnderungen dieser Erklärung\nDiese Datenschutzerklärung wird angepasst, sobald sich die tatsächliche Datenverarbeitung ändert — etwa bei Einführung von Nutzungsstatistiken, Absturzberichten oder einer Serverspeicherung von Arbeitsbereichen. Maßgeblich ist die jeweils in der App angezeigte Fassung.',

  'consent.title': 'Cookie-Einstellungen',
  'consent.intro': 'Kür speichert deinen Arbeitsbereich ausschließlich auf diesem Gerät. Sollten wir später Funktionen ergänzen, die Daten woanders hin senden, bleiben sie so lange deaktiviert, bis du sie hier erlaubst — und du kannst deine Wahl jederzeit auf dieser Seite ändern.',
  'consent.category.necessary.label': 'Notwendig',
  'consent.category.necessary.desc': 'Hält die App am Laufen: speichert deinen Arbeitsbereich auf diesem Gerät und merkt sich Sprache und Design. Immer aktiv.',
  'consent.category.statistics.label': 'Nutzungsstatistik',
  'consent.category.statistics.desc': 'Anonyme Nutzungsdaten, um zu sehen, welche Funktionen genutzt werden. Noch nicht umgesetzt — dieser Schalter hat bisher keine Wirkung.',
  'consent.category.crashReports.label': 'Absturz- und Fehlerberichte',
  'consent.category.crashReports.desc': 'Sendet Details zu Abstürzen, damit sie behoben werden können. Noch nicht umgesetzt — dieser Schalter hat bisher keine Wirkung.',
  'consent.rejectAll': 'Nur Notwendige',
  'consent.saveSelection': 'Auswahl speichern',
  'consent.acceptAll': 'Alle akzeptieren',
  'consent.privacyLink': 'Vollständige Datenschutzerklärung lesen',

  'registrationData.title': 'Meldedaten',
  'registrationData.lede': 'Diese Angaben stehen im Kopf beider XML-Dateien. Einmal erfassen, für alle Meldungen verwenden.',
  'registrationData.club.title': 'Meldender Verein',
  'meldedaten.field.club': 'Verein',
  'meldedaten.field.regionalFederation': 'Landesverband',
  'meldedaten.field.federation': 'Verband / Bezirk',
  'registrationData.contact.title': 'Verantwortlich / Fachwart',

  'field.firstName': 'Vorname',
  'field.lastName': 'Nachname',
  'field.uciId': 'UCI-ID',
  'field.email': 'E-Mail',
  'field.street': 'Straße',
  'field.phone': 'Telefon',
  'field.postalCode': 'PLZ',
  'field.city': 'Ort',
  'field.countryCode': 'Länderkennzeichen',
  'field.fax': 'Fax',
  'field.maxLength': 'max. {n}',

  'competitions.title': 'Wettbewerbe',
  'competitions.lede': 'Erfasse hier jeden Wettbewerb, für den du meldest. Auf dem Bildschirm „Meldungen" ordnest du jeder Meldung einen Wettbewerb, einen Starter und ein Programm zu.',
  'competitions.field.name': 'Wettbewerb',
  'competitions.field.date': 'Datum',
  'competitions.field.austragungsort': 'Austragungsort',
  'competitions.field.organiser': 'Veranstalter',
  'competitions.add': 'Wettbewerb hinzufügen',
  'competitions.empty.title': 'Noch keine Wettbewerbe',
  'competitions.empty.text': 'Lege einen Wettbewerb an und erfasse dann auf dem Bildschirm „Meldungen" die Meldungen dafür.',
  'competitions.unnamed': 'Unbenannter Wettbewerb',
  'competitions.form.title': 'Wettbewerb bearbeiten',
  'competitions.deleteConfirm': '{used} Meldung(en) sind für diesen Wettbewerb erfasst und werden mitgelöscht. Trotzdem löschen?',
  'competitions.deleted': 'Wettbewerb gelöscht.',

  'clubs.title': 'Vereine',
  'clubs.lede': 'Jeder Verein wird einmal angelegt und lässt sich danach bei Personen einfach auswählen.',
  'clubs.empty.title': 'Noch keine Vereine',
  'clubs.empty.text': 'Lege die teilnehmenden Vereine einmal an — danach lässt sich der Verein bei jeder Person nur noch auswählen.',
  'clubs.add': 'Verein anlegen',
  'clubs.unnamed': 'Ohne Namen',
  'clubs.row.number': 'Nr. {number}',
  'clubs.row.contact': 'Ansprechpartner: {name}',
  'clubs.deleteConfirm': '{used} Person(en) tragen diesen Verein bereits ein. Er bleibt dort als Text erhalten. Trotzdem löschen?',
  'clubs.deleted': 'Verein gelöscht.',
  'clubs.form.title': 'Verein bearbeiten',
  'clubs.field.name': 'Verein',
  'clubs.field.nation': 'Nation',
  'clubs.field.federation': 'Verband',
  'clubs.field.number': 'Nummer',
  'clubs.contact.title': 'Ansprechpartner',

  'athletes.title': 'Sportlerinnen und Sportler',
  'athletes.lede': 'Jede Person wird einmal angelegt und kann danach mehreren Startern zugeordnet werden.',
  'athletes.empty.title': 'Noch keine Personen',
  'athletes.empty.text': 'Lege zuerst die Sportlerinnen und Sportler an. Danach stellst du daraus Starter zusammen.',
  'athletes.add': 'Person hinzufügen',
  'athletes.unnamed': 'Ohne Namen',
  'athletes.deleteConfirm': 'Diese Person ist {count} Starter(n) zugeordnet. Trotzdem löschen?',
  'athletes.deleted': 'Person gelöscht.',
  'athletes.form.title': 'Person bearbeiten',
  'athletes.field.birthDate': 'Geburtsdatum',
  'athletes.field.nation': 'Nation',
  'athletes.field.sex': 'Geschlecht',
  'athletes.field.club': 'Verein',
  'athletes.club.none': '– kein Verein –',
  'athletes.club.emptyHint': 'noch keine Vereine angelegt',

  'starter.title': 'Starter',
  'starter.lede': 'Ein Starter ist ein Einzel, ein Zweier oder eine Vierer- bzw. Sechsermannschaft — mit Disziplin und den zugeordneten Personen. Programme und Meldungen werden getrennt zusammengestellt.',
  'starter.empty.title': 'Noch keine Starter',
  'starter.empty.text': 'Ein Starter verbindet eine Disziplin mit den Personen, die zusammen antreten.',
  'starter.add': 'Starter anlegen',
  'starter.unnamed': 'Neuer Starter',
  'starter.riderLimitToast': '{label} startet mit {max} Personen.',
  'starter.deleteConfirm': 'Dieser Starter wird in {used} Meldung(en) verwendet, die dabei mitgelöscht werden. Trotzdem löschen?',
  'starter.deleted': 'Starter gelöscht.',
  'starter.form.title': 'Starter bearbeiten',
  'starter.field.type': 'Art',
  'starter.field.discipline': 'Disziplin',
  'starter.field.startername': 'Startername',
  'starter.field.startername.hint': 'leer = aus den Nachnamen',
  'starter.personen.title': 'Personen',
  'starter.personen.needSportler': 'Lege zuerst unter <b>Sportler</b> Personen an.',
  'starter.personen.pick': 'Person',
  'starter.personen.pickPlaceholder': '– Person wählen –',
  'starter.riderTag': '{have} von {need} Personen',
  'starter.sgTag': 'SG',
  'starter.sexMismatchTag': 'Geschlecht passt nicht zur Disziplin',
  'starter.derived.club': 'Verein: <b>{club}</b>',
  'starter.derived.sg': ' · <b>Startgemeinschaft</b>',
  'starter.derived.sex': ' · Wertung als: <b>{sex}</b>',
  'starter.derived.note': 'Ergibt sich aus den Vereinen und dem Geschlecht der zugeordneten Personen.',
  'starter.derived.note.alias': 'Für den Export gekürzt aus dem vollen Vereinsnamen „{full}".',
  'starter.derived.note.autoTruncated': 'Für den Export automatisch auf {max} Zeichen gekürzt, wie bei WeBo2008. Für eine schönere Abkürzung unten einen eigenen kurzen Namen vergeben.',
  'starter.aliasTag': 'Vereinsname gekürzt',
  'starter.clubAlias.autoTruncated': 'Der zusammengesetzte Vereinsname „{full}" ist {len} Zeichen lang — der Export erlaubt nur {max}, deshalb wird er automatisch abgeschnitten. Für eine schönere Abkürzung unten einen kürzeren Namen vergeben.',
  'starter.clubAlias.aliasStillOverLimit': 'Der kurze Vereinsname ist noch {len} Zeichen lang — der Export erlaubt nur {max}. Bitte weiter kürzen.',
  'starter.field.clubAlias': 'Kurzer Vereinsname',
  'starter.field.clubAlias.hint': 'wird im Export statt des vollen Namens oben verwendet',
  'starter.sexMismatch.one': 'Diese Disziplin ist eine Frauen-Kategorie, aber {names} ist nicht als weiblich erfasst.',
  'starter.sexMismatch.many': 'Diese Disziplin ist eine Frauen-Kategorie, aber {names} sind nicht als weiblich erfasst.',
  'starter.personalBest.title': 'Persönliche Bestleistung',
  'starter.field.points': 'Punkte',
  'starter.field.date': 'Datum',
  'starter.field.venue': 'Ort',
  'starter.field.scoringReportEmail': 'E-Mail für den Wertungsbericht',

  'programme.title': 'Programme',
  'programme.titleWithName': 'Programm: {name}',
  'programme.editName': 'Programm umbenennen',
  'programme.select': 'Programm wählen',
  'programme.list.meta': '{count}/{limit} · {points} Pkt.',
  'programme.empty.title': 'Noch keine Programme',
  'programme.empty.text': 'Ein Programm gehört zu einer Starter-Art (Einzel, Zweier, Vierer- oder Sechsermannschaft) und einer Altersklasse, nicht zu einem bestimmten Starter — die Zuordnung zu Starter und Wettbewerb erfolgt auf dem Bildschirm „Meldungen".',
  'programme.new.button': 'Neues Programm',
  'programme.new.title': 'Neues Programm',
  'programme.new.type': 'Art',
  'programme.new.tier': 'Altersklasse',
  'programme.new.name': 'Name',
  'programme.new.namePlaceholder': 'z. B. „Kür A" — nur nötig, um mehrere zu unterscheiden',
  'programme.new.create': 'Anlegen',
  'programme.deleteProgramme': 'Programm löschen',
  'programme.deleteConfirm': 'Dieses Programm wird in {used} Meldung(en) verwendet, die dabei mitgelöscht werden. Trotzdem löschen?',
  'programme.deleted': 'Programm gelöscht.',
  'programme.duplicateProgramme': 'Programm duplizieren',
  'programme.duplicated': 'Programm dupliziert.',
  'programme.duplicateName': '{name} (Kopie)',
  'programme.readout.aufgestellt': 'Aufgestellt',
  'programme.readout.uebungen': 'Übungen',
  'programme.readout.profil': 'Schwierigkeitsverlauf',
  'programme.profileEmpty': 'noch leer',
  'programme.duplicate': 'Doppelt aufgestellt: <b>{list}</b>. Eine Übung zählt nur einmal — die Wiederholung bringt keine Punkte.',
  'programme.bikes.two': 'Auf zwei Rädern',
  'programme.bikes.one': 'Auf einem Rad',
  'programme.exercises.empty.title': 'Programm ist leer',
  'programme.exercises.empty.text': 'Wähle Übungen aus dem Katalog für {label}.',
  'programme.addExercise': 'Übung hinzufügen',
  'programme.clear': 'Programm leeren',
  'programme.clearConfirm': 'Alle Übungen aus diesem Programm entfernen?',
  'programme.cleared': 'Programm geleert.',
  'programme.share': 'Programm teilen',
  'programme.catalogueLoading': 'Figurenkatalog wird geladen …',
  'programme.figureNotFound': 'Figur nicht gefunden',
  'programme.tactical.title': 'Taktische Steigerung',
  'programme.tactical.upTo': 'T bis {points}',
  'programme.final.title': 'UCI 8.2.011: nur als letzte Übung des Programms oder unmittelbar vor dem Radwechsel zulässig',
  'programme.final.tag': 'Schlussfigur',
  'programme.dragHandle': 'Zum Sortieren ziehen',
  'programme.moveUp': 'Nach oben',
  'programme.moveDown': 'Nach unten',
  'programme.removeExercise': 'Übung entfernen',

  'entries.title': 'Meldungen',
  'entries.lede': 'Eine Meldung verbindet einen Starter mit einem Wettbewerb und einem passenden Programm. Jede Meldung exportiert die Kunstradmeldung/Teammeldung für ihren Wettbewerb.',
  'entries.empty.title': 'Noch keine Meldungen',
  'entries.empty.text': 'Lege eine Meldung an und wähle dann Wettbewerb, Starter und Programm.',
  'entries.add': 'Meldung hinzufügen',
  'entries.deleted': 'Meldung gelöscht.',
  'entries.field.comp': 'Wettbewerb',
  'entries.field.starter': 'Starter',
  'entries.field.programm': 'Programm',
  'entries.pickPlaceholder': '– wählen –',
  'entries.pickStarterFirst': 'zuerst Starter wählen',
  'entries.incomplete': 'Unvollständig',
  'entries.export': 'Export',
  'entries.unset': 'nicht gewählt',

  'share.imported': '„{name}“ aus einem geteilten Link importiert.',
  'share.importedWithSkipped':
    '„{name}“ importiert – {count} Übung(en) wurden nicht gefunden und übersprungen.',
  'share.decodeError': 'Dieser Link konnte nicht gelesen werden – er ist möglicherweise beschädigt oder stammt aus einer neueren App-Version.',

  'export.sheet.title': 'Export — {comp}',
  'export.check.title': 'Prüfung',
  'export.check.ok': 'Alle Pflichtangaben beider Schemas sind vollständig.',
  'export.loading': 'Figurenkatalog wird geladen …',
  'export.issue.view': 'ansehen',
  'export.kunstrad.title': 'Kunstradmeldung',
  'export.kunstrad.note': 'Starter, Sportler und Programm — kunstradmeldung.xsd, Version 19.0. {starters} Starter, {exercises} Übungen.',
  'export.downloadXml': 'XML herunterladen',
  'export.copyClipboard': 'In die Zwischenablage',
  'export.team.title': 'Teammeldung',
  'export.team.note': 'Jahresmeldung der Mannschaften — teammeldung.xsd, Version 1.1. {count} Team{plural}.',
  'export.team.empty': 'Sobald eine 2er-, 4er- oder 6er-Meldung angelegt ist, entsteht hier die Teammeldung.',
  'export.workspace.title': 'Arbeitsbereich',
  'export.workspace.note': 'Sichert Vereins-, Personen- und Programmdaten als Datei, die du später wieder öffnen kannst.',
  'export.workspace.noStorage': ' Dieser Browser speichert nichts automatisch — sichere regelmäßig.',
  'export.workspace.save': 'Arbeitsbereich sichern',
  'export.workspace.open': 'Arbeitsbereich öffnen',
  'export.workspace.import': 'Importieren (Auswahl)…',
  'export.workspace.exportSelection': 'Exportieren (Auswahl)…',
  'export.workspace.reset': 'Alles zurücksetzen',
  'export.resetConfirm': 'Alle Daten in diesem Browser löschen? Gesicherte Arbeitsbereichsdateien bleiben erhalten.',
  'export.resetDone': 'Zurückgesetzt.',

  'picker.title': 'Übung hinzufügen',
  'picker.searchPlaceholder': 'Nummer, Name oder Begriff suchen …',
  'picker.searchLabel': 'Übung suchen',
  'picker.groupLabel': 'Übungsgruppe',
  'picker.allGroups': 'Alle Gruppen',
  'picker.minPlaceholder': 'ab',
  'picker.maxPlaceholder': 'bis',
  'picker.minLabel': 'Punkte ab',
  'picker.maxLabel': 'Punkte bis',
  'picker.reset': 'Zurücksetzen',
  'picker.done': 'Fertig',
  'picker.loading': 'Übungskatalog wird geladen …',
  'picker.loadFailed': 'Der Übungskatalog konnte nicht geladen werden.',
  'picker.tooMany': 'Mehr als {limit} Übungen sind nicht zulässig.',
  'picker.added': '{id} hinzugefügt · {points} P.',
  'picker.tacticalTitle': 'Taktische Steigerung möglich',
  'picker.finalTitle': 'UCI 8.2.011: nur als Schlussfigur zulässig',
  'picker.noResults': 'Keine Übung passt zu dieser Suche. Andere Begriffe oder Filter probieren.',
  'picker.limitReached': 'Maximum von {limit} Übungen erreicht.',
  'picker.info': '{shown} Treffer · {have} von {limit} aufgestellt · {points} P.',

  'rules.count.exceeded': '{count} Übungen aufgestellt, erlaubt sind höchstens {max} (UCI {article}).',
  'rules.onBike.tooFew': '{count} Übungen auf einem Rad, vorgeschrieben sind mindestens {min} (UCI {article}).',
  'rules.onBike.tooMany': '{count} Übungen auf einem Rad, erlaubt sind höchstens {max} (UCI {article}).',
  'rules.onBike.needTwo': 'Das Programm braucht auch Übungen auf zwei Rädern — beides ist vorgeschrieben (UCI {article}).',
  'rules.final.single': 'Übung {nr} (#{pos}) ist eine Schlussfigur und darf nur als letzte Übung des Programms stehen (UCI 8.2.011).',
  'rules.final.pair': 'Übung {nr} (#{pos}) ist eine Schlussfigur und darf nur als letzte Übung des Programms oder unmittelbar vor dem Radwechsel stehen (UCI 8.2.011).',
  'rules.raiser.continuity': 'Übung {nr} (#{pos}) endet im {label} — die folgende Übung muss im selben Steiger fortgesetzt werden (UCI 8.2.028).',
  'rules.bikes.multipleSwitches': 'Übung {nr} (#{pos}) wechselt erneut zwischen einem und zwei Rädern — ein Paarprogramm darf nur einmal von einem zum anderen wechseln (UCI 8.2.006).',
  'rules.figure.repeated': 'Übung {nr} (#{pos}) wiederholt Figur {first} (#{firstPos}) — jede Figur darf im Programm nur einmal vorkommen (UCI 8.1.019).',

  'validate.meta.clubMissing': 'Meldedaten: der meldende Verein fehlt.',
  'validate.meta.clubTooLong': 'Meldedaten: Vereinsname ist zu lang',
  'validate.meta.federationTooLong': 'Meldedaten: Verband ist zu lang',
  'validate.contact.firstNameMissing': 'Verantwortlich: Vorname fehlt.',
  'validate.contact.lastNameMissing': 'Verantwortlich: Nachname fehlt.',
  'validate.contact.emailMissing': 'Verantwortlich: E-Mail fehlt.',
  'validate.contact.emailTooLong': 'Verantwortlich: E-Mail ist zu lang',
  'validate.contact.uciIdTooLong': 'Verantwortlich: UCI-ID ist zu lang',
  'validate.comp.nameMissing': 'Wettbewerb: Name fehlt.',
  'validate.comp.dateMissing': 'Wettbewerb: Datum fehlt.',
  'validate.comp.venueMissing': 'Wettbewerb: Austragungsort fehlt.',
  'validate.comp.dateInvalid': 'Wettbewerb: Datum ist kein gültiges Datum.',
  'validate.comp.noneDefined': 'Es ist noch kein Wettbewerb angelegt.',
  'validate.comp.noneSelected': 'Für den Export ist kein Wettbewerb ausgewählt.',
  'validate.noStarters': 'Für diesen Wettbewerb ist kein Starter angelegt.',
  'validate.entry.incomplete': '{count} Meldung(en) fehlt noch ein Starter oder ein Programm — sie werden nicht exportiert.',
  'validate.entry.duplicateStarter': '{who} ist {count}-mal für diesen Wettbewerb gemeldet — jeder Starter darf nur einmal vorkommen.',
  'validate.entry.typeMismatch': '{who} ist ein {starterType}, das gewählte Programm ist aber für {programType} — wähle ein passendes Programm.',
  'validate.starter.riderCount': '{who}: {count} Personen zugeordnet, {label} braucht {needed}.',
  'validate.starter.sexMismatch.one': '{who}: Disziplin ist eine Frauen-Kategorie, aber {names} ist nicht als weiblich erfasst.',
  'validate.starter.sexMismatch.many': '{who}: Disziplin ist eine Frauen-Kategorie, aber {names} sind nicht als weiblich erfasst.',
  'validate.starter.nameTooLong': '{who}: Startername ist zu lang',
  'validate.starter.clubTooLong': '{who}: Vereinsname (aus den Vereinen der Personen) ist zu lang',
  'validate.starter.disciplineTooLong': '{who}: Disziplin ist zu lang',
  'validate.starter.personalBestTooLong': '{who}: Bestleistung ist zu lang',
  'validate.starter.scoringReportEmailTooLong': '{who}: E-Mail Wertungsbericht ist zu lang',
  'validate.starter.bestDateInvalid': '{who}: Datum der Bestleistung ist kein gültiges Datum.',
  'validate.programm.empty': '{who}: das Programm ist leer.',
  'validate.programm.tooMany': '{who}: {count} Übungen aufgestellt, erlaubt sind höchstens {max}.',
  'validate.uebung.notFound': '{who}: Übung {nr} ist nicht im Figurenkatalog enthalten.',
  'validate.uebung.duplicate': '{who}: Übung {nr} ist mehrfach aufgestellt und zählt nur einmal.',
  'validate.athlete.missing': '{who}: eine zugeordnete Person existiert nicht mehr.',
  'validate.athlete.nameMissing': '{who}: {name} — Vor- oder Nachname fehlt.',
  'validate.athlete.uciIdMissing': '{who}: {name} — UCI-ID fehlt.',
  'validate.athlete.uciIdTooLong': '{who}: {name} — UCI-ID ist zu lang',
  'validate.athlete.birthDateInvalid': '{who}: {name} — Geburtsdatum fehlt oder ist ungültig.',
  'validate.athlete.nationInvalid': '{who}: {name} — Nation „{nation}" ist im Schema nicht zulässig.',
  'validate.singlesWarning': '{count} Einzelstarter erscheinen nur in der Kunstradmeldung — die Teammeldung kennt keine 1er-Disziplinen.',
  'validate.noTeams': 'Keine Mannschaft angelegt — es entsteht nur eine Kunstradmeldung.',
  'validate.maxChars': ' (max. {n} Zeichen).',

  'lib.downloaded': '{filename} heruntergeladen.',
  'lib.downloadFailedCopied': 'Download nicht möglich — Inhalt in die Zwischenablage kopiert.',
  'lib.copiedClipboard': 'In die Zwischenablage kopiert.',
  'lib.copyFailed': 'Kopieren nicht möglich — Text im Vorschaufeld markieren.',

  'ie.title.import': 'Auswahl importieren',
  'ie.title.export': 'Auswahl exportieren',
  'ie.import.pick': 'Datei zum Importieren wählen…',
  'ie.import.invalid': 'Diese Datei lässt sich nicht als Arbeitsbereich lesen.',
  'ie.import.noneSelected': 'Nichts ausgewählt.',
  'ie.import.done': '{clubs} Vereine, {athletes} Personen, {comps} Wettbewerbe, {starters} Starter, {programs} Programme, {entries} Meldungen importiert ({skipped} bereits vorhanden, übersprungen).',
  'ie.export.done': 'Exportiert.',
  'ie.section.clubs': 'Vereine',
  'ie.section.athletes': 'Sportler',
  'ie.section.comps': 'Wettbewerbe',
  'ie.section.starters': 'Starter',
  'ie.section.programs': 'Programme',
  'ie.section.entries': 'Meldungen',
  'ie.section.empty': 'keine',
  'ie.item.starter': '{name} ({type})',
  'ie.item.program': '{name} ({count} Übungen)',
  'ie.item.entry': '{starter} — {comp}',
  'ie.expand': 'Ausklappen',
  'ie.collapse': 'Einklappen',
  'ie.selectAll': 'Alle auswählen',
  'ie.selectNone': 'Keine auswählen',
  'ie.confirm.import': 'Importieren',
  'ie.confirm.export': 'Exportieren',

  'webo.title': 'WeBo2008-Import',
  'webo.import.pick': 'WeBo-XML-Datei wählen…',
  'webo.import.invalid': 'Diese Datei lässt sich nicht als WeBo2008-Export lesen (weder Kunstradmeldung noch Teammeldung).',
  'webo.import.noneSelected': 'Nichts ausgewählt.',
  'webo.import.done':
    '{comps} Wettbewerb, {starters} Starter ({athletesNew} neue Personen, {athletesReused} bereits bekannt — davon {athletesFilled} um fehlende Angaben ergänzt), {clubsNew} neue Vereine, {programs} Programme, {entries} Meldungen importiert.',
  'webo.category.registrationData': 'Meldedaten',
  'webo.category.registrationData.hint': 'Verantwortlicher, Verein und Landesverband — überschreibt die aktuellen Meldedaten',
  'webo.category.wettbewerb': 'Wettbewerb',
  'webo.category.wettbewerb.unavailable': 'Diese Datei enthält keine Wettbewerbsdaten (Teammeldung hat keine).',
  'webo.category.starter': 'Starter',
  'webo.category.programme': 'Programme',
  'webo.category.programme.unavailable': 'Diese Datei enthält keine Programmdaten (Teammeldung hat keine).',
  'webo.item.entry': '{name} — {discipline}',
  'webo.item.programme.ok': '{count} Figuren, {points} Pkt.',
  'webo.item.programme.mismatch': '{count} Figuren ({unmatched} unbekannt), Punkte weichen ab ({rebuilt} statt {declared})',
  'webo.warnings.title': '{count} Auffälligkeit(en):',
  'webo.warning.unknownNation': '{name}: Nation „{nation}" ist nicht bekannt.',
  'webo.warning.unknownRegionalFederation': 'Landesverband „{value}" ist nicht bekannt.',
  'webo.warning.unknownDiscipline': 'Disziplin „{discipline}" nicht erkannt — Startart aus {count} Person(en) geschätzt.',
  // ------------------------------------------------------------------- pwa
  'pwa.offlineReady': 'Kür ist jetzt offline nutzbar.',
  'pwa.updateAvailable': 'Eine neue Version ist verfügbar.',
  'pwa.reload': 'Neu laden',
};

export default de;
